import React, { useState, useCallback } from 'react'
import { ItineraryDay } from '../../utils/itineraryHelpers'
import { ItineraryWaypointItem } from '../ItineraryWaypointItem'
import { WaypointResponse } from '@/shared/api/routes'
import { Plus } from '@/ui/icons'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface ItineraryDaySectionProps {
  day: ItineraryDay
  startWaypointNumber: number
  onAddWaypoint?: (day: number) => void
  onDeleteWaypoint?: (waypointId: string) => Promise<void>
  onUpdateWaypointDay?: (waypointId: string, day: number) => Promise<void>
  onUpdateWaypoint?: (waypointId: string, updates: { poi_type?: string | null; poi_custom_label?: string | null; name?: string }) => Promise<void>
  onReorderWaypoints?: (waypointIds: string[]) => Promise<void>
  allWaypoints?: WaypointResponse[] // All waypoints for the trip (needed for day-scoped reordering)
  totalDays?: number
  selectedWaypointId?: string | null
  onWaypointSelect?: (waypoint: WaypointResponse) => void
}

interface SortableWaypointWrapperProps {
  item: { type: 'waypoint'; data: WaypointResponse }
  waypointNumber: number
  onDeleteWaypoint?: (waypointId: string) => Promise<void>
  onUpdateDay?: (waypointId: string, day: number) => Promise<void>
  onUpdateWaypoint?: (waypointId: string, updates: { poi_type?: string | null; poi_custom_label?: string | null; name?: string }) => Promise<void>
  totalDays?: number
  isSelected?: boolean
  onWaypointSelect?: (waypoint: WaypointResponse) => void
  isDragging?: boolean
}

function SortableWaypointWrapper({
  item,
  waypointNumber,
  onDeleteWaypoint,
  onUpdateDay,
  onUpdateWaypoint,
  totalDays,
  isSelected,
  onWaypointSelect,
  isDragging
}: SortableWaypointWrapperProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: item.data.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ItineraryWaypointItem
        waypoint={item.data}
        number={waypointNumber}
        onDelete={onDeleteWaypoint}
        onUpdateDay={onUpdateDay}
        onUpdateWaypoint={onUpdateWaypoint}
        currentDay={item.data.trip_day ?? 1}
        totalDays={totalDays}
        isSelected={isSelected}
        onClick={onWaypointSelect}
        isDraggable={true}
        dragAttributes={attributes}
        dragListeners={listeners}
      />
    </div>
  )
}

export function ItineraryDaySection({ 
  day, 
  startWaypointNumber, 
  onAddWaypoint,
  onDeleteWaypoint,
  onUpdateWaypointDay,
  onUpdateWaypoint,
  onReorderWaypoints,
  allWaypoints = [],
  totalDays,
  selectedWaypointId,
  onWaypointSelect
}: ItineraryDaySectionProps) {
  const hasWaypoints = day.waypoints.length > 0
  const hasContent = hasWaypoints
  const [activeId, setActiveId] = useState<string | null>(null)

  // Only enable drag-and-drop if there are multiple waypoints in this day
  const canDragAndDrop = hasWaypoints && day.waypoints.length > 1 && onReorderWaypoints

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }, [])

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over || active.id === over.id || !onReorderWaypoints || !allWaypoints.length) {
      return
    }

    // Get waypoint IDs for this day (sorted by order_index) - compute fresh to avoid stale closure
    const dayWaypointIds = day.waypoints
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
      .map(wp => wp.id)

    const oldIndex = dayWaypointIds.indexOf(active.id as string)
    const newIndex = dayWaypointIds.indexOf(over.id as string)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    // Reorder waypoints within this day
    const reorderedDayWaypointIds = arrayMove(dayWaypointIds, oldIndex, newIndex)

    // Reconstruct full trip waypoint order
    // Group all waypoints by day, then update this day's order
    const waypointsByDay = new Map<number, WaypointResponse[]>()
    allWaypoints.forEach(wp => {
      const wpDay = wp.trip_day ?? 1
      if (!waypointsByDay.has(wpDay)) {
        waypointsByDay.set(wpDay, [])
      }
      waypointsByDay.get(wpDay)!.push(wp)
    })

    // Build the full ordered array: all days in order, with this day's waypoints reordered
    const fullOrderedIds: string[] = []
    const allDays = Array.from(waypointsByDay.keys()).sort((a, b) => a - b)

    for (const dayNum of allDays) {
      if (dayNum === day.day) {
        // Use the reordered waypoint IDs for this day
        fullOrderedIds.push(...reorderedDayWaypointIds)
      } else {
        // Use original order for other days
        const otherDayWaypoints = waypointsByDay.get(dayNum)!
          .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        fullOrderedIds.push(...otherDayWaypoints.map(wp => wp.id))
      }
    }

      // Call the reorder handler with full trip order
      try {
        await onReorderWaypoints(fullOrderedIds)
      } catch (error) {
      }
  }, [day.waypoints, day.day, onReorderWaypoints, allWaypoints])

  // Format date for display: "Day 1 Friday, Nov 29, 2025"
  const formatDayHeader = (): string => {
    const dateStr = day.dateRange || day.date
    
    // If dateStr already contains "Day X", use it as is
    if (dateStr.startsWith('Day ')) {
      return dateStr
    }
    
    // Parse the date string (format: "Friday, Nov 29")
    // Extract year from trip dates if available, otherwise use current year
    const currentYear = new Date().getFullYear()
    const dateMatch = dateStr.match(/([A-Za-z]+day), ([A-Za-z]+) (\d+)/)
    
    if (dateMatch) {
      const [, dayName, month, dayNum] = dateMatch
      return `Day ${day.day} ${dayName}, ${month} ${dayNum}, ${currentYear}`
    }
    
    // Fallback
    return `Day ${day.day} ${dateStr}`
  }

  // Format duration: "1h 25m" or "45m"
  const formatDuration = (minutes: number): string => {
    if (minutes === 0) return '0m'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }
    return `${mins}m`
  }

  // Format distance: "35.0 mi"
  const formatDistance = (miles: number): string => {
    if (miles === 0) return '0.0 mi'
    return `${miles.toFixed(1)} mi`
  }

  return (
    <div className="mb-8">
      {/* Day header with date and summary statistics */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {/* Date text (no pill styling) */}
          <span className="text-body-sm font-medium text-earth-900">
            {formatDayHeader()}
          </span>
        </div>
        
        {/* Summary statistics bar */}
        {hasWaypoints && (
          <div className="flex items-center gap-3 text-body-sm text-earth-600">
            <span>{day.waypointCount} {day.waypointCount === 1 ? 'stop' : 'stops'}</span>
            <span>•</span>
            <span>{formatDistance(day.totalDistance)}</span>
            <span>•</span>
            <span>{formatDuration(day.totalDuration)} driving</span>
          </div>
        )}
        
        {/* Add waypoint button */}
        <button
          className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-colors"
          aria-label="Add waypoint"
          onClick={() => onAddWaypoint?.(day.day)}
          type="button"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Waypoints and items list */}
      {hasContent ? (
        canDragAndDrop ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={day.waypoints
                .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                .map(wp => wp.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-0">
                {day.waypoints.map((waypoint, index) => {
                  const waypointNumber = startWaypointNumber + index
                  const isDragging = activeId === waypoint.id

                  return (
                    <SortableWaypointWrapper
                      key={waypoint.id}
                      item={{ type: 'waypoint', data: waypoint }}
                      waypointNumber={waypointNumber}
                      onDeleteWaypoint={onDeleteWaypoint}
                      onUpdateDay={onUpdateWaypointDay}
                      onUpdateWaypoint={onUpdateWaypoint}
                      totalDays={totalDays}
                      isSelected={selectedWaypointId === waypoint.id}
                      onWaypointSelect={onWaypointSelect}
                      isDragging={isDragging}
                    />
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="space-y-0">
            {day.waypoints.map((waypoint, index) => {
              const waypointNumber = startWaypointNumber + index

              return (
                <ItineraryWaypointItem
                  key={waypoint.id}
                  waypoint={waypoint}
                  number={waypointNumber}
                  onDelete={onDeleteWaypoint}
                  onUpdateDay={onUpdateWaypointDay}
                  onUpdateWaypoint={onUpdateWaypoint}
                  currentDay={day.day}
                  totalDays={totalDays}
                  isSelected={selectedWaypointId === waypoint.id}
                  onClick={onWaypointSelect}
                  isDraggable={false}
                />
              )
            })}
          </div>
        )
      ) : (
        <div className="border-2 border-dashed border-earth-200 rounded-lg p-6 text-center">
          <p className="text-body-sm text-earth-600">
            No waypoints planned for this day
          </p>
        </div>
      )}
    </div>
  )
}

